// maskBurn.ts — the PII mask projection + rasterization math (EGO-163).
//
// A 1:1 port of tools/mask_burnin.py (the Mac-side reference ORACLE), which is
// itself a port of viewer/viewer.html `makeEyeProjector()` / `buildCamFromMeta()`.
// Three implementations, one frame chain: if the viewer draws an overlay on a
// thing, the oracle masks that thing, and so does this. core/test/maskburn.test.ts
// diffs this against the oracle per frame; keep them edit-locked.
//
// This module is PURE (no DOM, no node, no mediabunny) so the parity tests can
// import it — all mediabunny video I/O lives in maskBurnVideo.ts. Session access
// goes through SessionSource, so it reads a directory on disk (node) and a
// FileSystemDirectoryHandle (browser) identically, like the QA gate.
//
// Pipeline per frame, per eye:
//   masks.json quad (xr_origin_local, Unity left-handed, metres)
//     --triangle fan over corners in stored order--> triangles
//     --16 samples/edge--> polyline (world)
//     --camL/camR for this frame, paired BY TIMESTAMP--> camera space
//     --Sutherland-Hodgman near clip at z=0.02, BEFORE projecting--> visible poly
//     --intrinsics + Camera2 distortion--> image polygon
//     --even-odd scanline fill + 8px box dilation--> boolean mask
//
// FAIL CLOSED is the whole point: a missing/invalid pose, a pairing gap wider
// than the tolerance, or a non-finite projection blacks the ENTIRE frame. Never
// leave a frame unmasked because data was missing.

import type { SessionSource } from "./source.ts";
import { parseInt64LE } from "./pyhelpers.ts";

// Ports of viewer/viewer.html constants — do not retune independently.
export const NEAR = 0.02; // camera-space near plane (makeEyeProjector)
export const R2_MAX = 1.5; // distortion radius clamp, see project()
export const EDGE_SUBDIV = 16; // samples per quad edge, so distortion-curved edges are followed
export const DEFAULT_DILATE_PX = 8;
export const DEFAULT_MAX_DT_MS = 50;

export type Eye = "left" | "right";
export type Vec3 = [number, number, number];
export type Vec2 = [number, number];

export interface MaskRegionMeta {
  id: string;
  name?: string | null;
  corners: number[][];
}

export interface MaskDoc {
  schema?: number;
  space?: string | null;
  regions: MaskRegionMeta[];
}

/** Normalized intrinsics: projection emits 0..1 coords, scaled by w/h at the end. */
export interface Cam {
  fx: number;
  fy: number;
  cx: number;
  cy: number;
  /** [k1,k2,k3,t1,t2] Camera2 LENS_DISTORTION order, or null → pure pinhole. */
  dist: number[] | null;
  w: number;
  h: number;
}

/** One region, pre-expanded into its subdivided triangle fan (world space). */
export interface PreparedRegion {
  id: string;
  name: string | null;
  /** Each entry is a flat [x,y,z,...] polyline of EDGE_SUBDIV*3 points. */
  tris: Float64Array[];
}

export interface CamTrack {
  /** elapsedRealtimeNanos, ascending (stable-sorted). */
  t: Float64Array;
  /** 7 floats per sample: px,py,pz,qx,qy,qz,qw (zeros where invalid). */
  pose: Float64Array;
  valid: Uint8Array;
}

// ─────────────────────────────────────────────────────────────────────────────
// Session loading
// ─────────────────────────────────────────────────────────────────────────────

/** Read + validate masks.json. Throws on anything this port cannot mask safely. */
export function parseMasks(text: string): MaskDoc {
  const doc = JSON.parse(text) as MaskDoc;
  const space = doc?.space;
  if (space != null && space !== "xr_origin_local") {
    throw new Error(`unsupported mask space ${JSON.stringify(space)}`);
  }
  const regions = doc?.regions ?? [];
  if (!regions.length) throw new Error("masks.json has no regions");
  for (const r of regions) {
    const c = r?.corners;
    if (!Array.isArray(c) || c.length < 3 || c.some((p) => !Array.isArray(p) || p.length !== 3)) {
      throw new Error(`region ${r?.id}: corners must be >=3 [x,y,z] points`);
    }
  }
  return { ...doc, regions };
}

/**
 * Port of viewer.html buildCamFromMeta(): active-array intrinsics → normalized.
 *
 * meta `*_intrinsics` = [fx, fy, cx, cy, s] in ACTIVE ARRAY pixels; rescale into
 * the capture frame, then normalize by capture size.
 */
export function buildCam(meta: any, eye: Eye): Cam {
  const ic = meta?.[`${eye}_intrinsics`];
  if (!Array.isArray(ic) || ic.length < 4) {
    throw new Error(`meta.json has no ${eye}_intrinsics — cannot project`);
  }
  const capW = Number(meta[`${eye}_width`]);
  const capH = Number(meta[`${eye}_height`]);
  if (!Number.isFinite(capW) || !Number.isFinite(capH) || capW <= 0 || capH <= 0) {
    throw new Error(`meta.json has no usable ${eye}_width/${eye}_height`);
  }
  const aa = meta[`${eye}_active_array`] ?? [capW, capH];
  const sx = capW / aa[0];
  const sy = capH / aa[1];
  const d = meta[`${eye}_distortion`];
  return {
    fx: (ic[0] * sx) / capW,
    fy: (ic[1] * sy) / capH,
    cx: (ic[2] * sx) / capW,
    cy: (ic[3] * sy) / capH,
    // [k1,k2,k3,t1,t2] in Camera2 LENS_DISTORTION order (NOT OpenCV's
    // [k1,k2,p1,p2,k3]) on f-normalized coords — dimensionless, so no rescale.
    // Frequently null in real sessions → pure pinhole.
    dist: Array.isArray(d) && d.length >= 5 ? d.slice(0, 5).map(Number) : null,
    w: capW,
    h: capH,
  };
}

/**
 * tracking.jsonl → this eye's per-frame camera world pose track.
 *
 * `camL`/`camR` are the true per-frame camera world poses in xr_origin_local
 * ([px,py,pz,qx,qy,qz,qw]); they are null whenever the camera wasn't streaming.
 */
export async function loadCamTrack(src: SessionSource, eye: Eye): Promise<CamTrack> {
  const key = eye === "left" ? "camL" : "camR";
  const ts: number[] = [];
  const poses: number[] = [];
  const valid: number[] = [];
  for await (const raw of src.lines("tracking.jsonl")) {
    const line = raw.trim();
    if (!line) continue;
    let o: any;
    try { o = JSON.parse(line); } catch { continue; }
    const t = o?.t;
    if (t === undefined || t === null) continue;
    const c = o[key];
    const ok = Array.isArray(c) && c.length === 7 &&
      c.every((v: unknown) => typeof v === "number" && Number.isFinite(v));
    ts.push(Number(t));
    for (let i = 0; i < 7; i++) poses.push(ok ? Number(c[i]) : 0);
    valid.push(ok ? 1 : 0);
  }
  if (!ts.length) throw new Error("no usable tracking samples in tracking.jsonl");
  // Stable sort by t (numpy argsort kind="stable").
  const order = ts.map((_, i) => i);
  order.sort((a, b) => (ts[a] - ts[b]) || (a - b));
  const t = new Float64Array(ts.length);
  const pose = new Float64Array(ts.length * 7);
  const vv = new Uint8Array(ts.length);
  for (let i = 0; i < order.length; i++) {
    const j = order[i];
    t[i] = ts[j];
    vv[i] = valid[j];
    for (let k = 0; k < 7; k++) pose[i * 7 + k] = poses[j * 7 + k];
  }
  return { t, pose, valid: vv };
}

/** Read an int64-LE `<eye>.timestamps` sidecar (one per emitted video frame). */
export async function loadTimestamps(src: SessionSource, eye: Eye): Promise<number[]> {
  return parseInt64LE(await src.readBytes(`${eye}.timestamps`));
}

/**
 * Nearest-neighbour pairing BY TIMESTAMP (never by index — the two streams run
 * at different rates and either can drop).
 */
export function pairFrames(
  vidTs: ArrayLike<number>,
  trkTs: ArrayLike<number>,
  offsetNs: number,
  maxDtNs: number,
): { idx: Int32Array; inTol: Uint8Array } {
  const n = vidTs.length;
  const m = trkTs.length;
  const idx = new Int32Array(n);
  const inTol = new Uint8Array(n);
  for (let f = 0; f < n; f++) {
    const q = vidTs[f] + offsetNs;
    // np.searchsorted(trkTs, q) — leftmost insertion point.
    let lo = 0, hi = m;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (trkTs[mid] < q) lo = mid + 1; else hi = mid;
    }
    const a = Math.min(Math.max(lo - 1, 0), m - 1);
    const b = Math.min(Math.max(lo, 0), m - 1);
    const pick = Math.abs(trkTs[a] - q) <= Math.abs(trkTs[b] - q) ? a : b;
    idx[f] = pick;
    inTol[f] = Math.abs(trkTs[pick] - q) <= maxDtNs ? 1 : 0;
  }
  return { idx, inTol };
}

// ─────────────────────────────────────────────────────────────────────────────
// Geometry (port of makeEyeProjector)
// ─────────────────────────────────────────────────────────────────────────────

/** Sample each edge of a closed polygon n times, so projected edges follow the
 *  distortion curve. With null distortion this is a no-op on the filled shape,
 *  but device-stamped coefficients exist (tools/calibrate/stamp_meta.py). */
export function subdivide(poly: number[][], n = EDGE_SUBDIV): Float64Array {
  const k = poly.length;
  const out = new Float64Array(k * n * 3);
  let w = 0;
  for (let i = 0; i < k; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % k];
    for (let s = 0; s < n; s++) {
      const t = s / n;
      out[w++] = a[0] + (b[0] - a[0]) * t;
      out[w++] = a[1] + (b[1] - a[1]) * t;
      out[w++] = a[2] + (b[2] - a[2]) * t;
    }
  }
  return out;
}

/**
 * A mask is the triangle fan (c0,c1,c2)+(c0,c2,c3) over the corners **in pinch
 * order** — MaskRenderer.cs k_Tris, and what docs/DATA_FLOW.md defines.
 * Rasterizing the fan (not a perimeter polygon) is what makes this match the
 * headset preview even when the pinch order produces a self-crossing quad.
 */
export function fanTriangles(corners: number[][]): Float64Array[] {
  const out: Float64Array[] = [];
  for (let i = 1; i < corners.length - 1; i++) {
    out.push(subdivide([corners[0], corners[i], corners[i + 1]]));
  }
  return out;
}

export function prepareRegions(regions: MaskRegionMeta[]): PreparedRegion[] {
  return regions.map((r) => ({
    id: r.id,
    name: r.name ?? null,
    tris: fanTriangles(r.corners.map((p) => p.map(Number))),
  }));
}

/** Rotation matrix (row-major 9) for the INVERSE of quaternion q=[x,y,z,w]. */
function quatConjMatrix(q: Float64Array, o: number): Float64Array {
  const x = -q[o], y = -q[o + 1], z = -q[o + 2], w = q[o + 3];
  return new Float64Array([
    1 - 2 * (y * y + z * z), 2 * (x * y - z * w), 2 * (x * z + y * w),
    2 * (x * y + z * w), 1 - 2 * (x * x + z * z), 2 * (y * z - x * w),
    2 * (x * z - y * w), 2 * (y * z + x * w), 1 - 2 * (x * x + y * y),
  ]);
}

/**
 * Pcam = qInv * (Pworld − camPos), in place into `out`.
 *
 * camL/camR are already Unity convention (+Z forward, +Y up), so the Android
 * Y/Z flip in the viewer's toCam() does NOT apply.
 */
export function worldToCam(ptsW: Float64Array, pose: Float64Array, poseOff: number): Float64Array {
  const R = quatConjMatrix(pose, poseOff + 3);
  const px = pose[poseOff], py = pose[poseOff + 1], pz = pose[poseOff + 2];
  const out = new Float64Array(ptsW.length);
  for (let i = 0; i < ptsW.length; i += 3) {
    const x = ptsW[i] - px, y = ptsW[i + 1] - py, z = ptsW[i + 2] - pz;
    out[i] = R[0] * x + R[1] * y + R[2] * z;
    out[i + 1] = R[3] * x + R[4] * y + R[5] * z;
    out[i + 2] = R[6] * x + R[7] * y + R[8] * z;
  }
  return out;
}

/**
 * Sutherland-Hodgman clip of a camera-space polygon against z >= NEAR.
 *
 * Done BEFORE projecting (as the viewer's projectSeg does per-segment) so a quad
 * you are standing next to masks its visible part instead of vanishing or
 * folding across the frame. Routinely produces an ODD vertex count.
 */
export function clipNear(poly: Float64Array): Float64Array {
  const n = poly.length / 3;
  if (n === 0) return poly;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = i * 3;
    const b = ((i + 1) % n) * 3;
    const aIn = poly[a + 2] >= NEAR;
    const bIn = poly[b + 2] >= NEAR;
    if (aIn) out.push(poly[a], poly[a + 1], poly[a + 2]);
    if (aIn !== bIn) {
      const t = (NEAR - poly[a + 2]) / (poly[b + 2] - poly[a + 2]);
      out.push(
        poly[a] + (poly[b] - poly[a]) * t,
        poly[a + 1] + (poly[b + 1] - poly[a + 1]) * t,
        poly[a + 2] + (poly[b + 2] - poly[a + 2]) * t,
      );
    }
  }
  return new Float64Array(out);
}

/** Camera space (+Z fwd, +Y up) → pixel coords, via the sensor frame (+Y down). */
export function project(polyC: Float64Array, cam: Cam): Float64Array {
  const n = polyC.length / 3;
  const out = new Float64Array(n * 2);
  const d = cam.dist;
  for (let i = 0; i < n; i++) {
    const z = polyC[i * 3 + 2];
    let nx = polyC[i * 3] / z;
    let ny = -polyC[i * 3 + 1] / z;
    if (d) {
      const [k1, k2, k3, t1, t2] = d;
      // The Brown-Conrady polynomial is only valid inside the calibrated field
      // (image corners sit at r² ≈ 1.1). Past that the r⁶ term diverges and folds
      // points back ACROSS the frame — a masked region would smear or teleport.
      // Freezing the radius keeps out-of-FOV points monotonically out-of-frame.
      // Load-bearing; see viewer.html ~5806-5815.
      const r2 = Math.min(nx * nx + ny * ny, R2_MAX);
      const rad = 1 + r2 * (k1 + r2 * (k2 + r2 * k3));
      const xd = nx * rad + 2 * t1 * nx * ny + t2 * (r2 + 2 * Math.min(nx * nx, R2_MAX));
      const yd = ny * rad + t1 * (r2 + 2 * Math.min(ny * ny, R2_MAX)) + 2 * t2 * nx * ny;
      nx = xd;
      ny = yd;
    }
    out[i * 2] = (cam.fx * nx + cam.cx) * cam.w;
    out[i * 2 + 1] = (cam.fy * ny + cam.cy) * cam.h;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rasterization
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Even-odd scanline fill of a pixel-space polygon (flat [x,y,...]) into `mask`
 * (w*h, 1 = masked). ORs into whatever is already there.
 *
 * Half-open edge convention: an edge crosses row yc iff exactly one endpoint is
 * <= yc, so a vertex sitting exactly on a scanline is counted once, not twice,
 * and a local min/max there contributes zero crossings. Exactly-horizontal edges
 * never cross and are dropped outright (they would also divide by zero).
 */
export function fillPolygon(pts: Float64Array, w: number, h: number, mask: Uint8Array): boolean {
  const n = pts.length / 2;
  if (n < 3) return false;
  let minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < pts.length; i++) {
    if (!Number.isFinite(pts[i])) return false;
    if (i & 1) { if (pts[i] < minY) minY = pts[i]; if (pts[i] > maxY) maxY = pts[i]; }
  }
  const y0 = Math.max(0, Math.floor(minY - 0.5));
  const y1 = Math.min(h, Math.ceil(maxY + 0.5) + 1);
  if (y1 <= y0) return false;
  // Crossings per row are even by construction, but the EDGE count is not:
  // near-plane clipping routinely yields an odd-vertex polygon (EGO-163 repro:
  // frame 488 of 20260825_141428 clips to 49 vertices), so the pair count is
  // capped at n>>1 rather than assuming an even vertex count.
  const maxPairs = n >> 1;
  const xs = new Float64Array(n);
  let touched = false;
  for (let y = y0; y < y1; y++) {
    const yc = y + 0.5;
    let count = 0;
    for (let i = 0; i < n; i++) {
      const ax = pts[i * 2], ay = pts[i * 2 + 1];
      const j = (i + 1) % n;
      const bx = pts[j * 2], by = pts[j * 2 + 1];
      if (by === ay) continue; // horizontal edge: never crosses
      if ((ay <= yc) === (by <= yc)) continue;
      xs[count++] = ax + (yc - ay) * ((bx - ax) / (by - ay));
    }
    if (count < 2) continue;
    const row = xs.subarray(0, count);
    row.sort();
    const pairs = Math.min(maxPairs, count >> 1);
    const base = y * w;
    for (let p = 0; p < pairs; p++) {
      // pixel centre in [xs, xe) → index range [ceil(xs-0.5), ceil(xe-0.5))
      const i0 = Math.min(Math.max(Math.ceil(row[2 * p] - 0.5), 0), w);
      const i1 = Math.min(Math.max(Math.ceil(row[2 * p + 1] - 0.5), 0), w);
      if (i1 > i0) {
        mask.fill(1, base + i0, base + i1);
        touched = true;
      }
    }
  }
  return touched;
}

/**
 * Square-box dilation by `px`, in place, restricted to [x0,x1)×[y0,y1).
 *
 * A square over-covers a disc slightly — deliberate: over-masking is the safe
 * direction for a privacy tool. Separable (two 1D box-any passes), and the
 * window is treated as zero-padded, exactly like the oracle's np.pad.
 */
export function dilateRect(
  mask: Uint8Array, w: number, _h: number,
  x0: number, y0: number, x1: number, y1: number, px: number,
): void {
  if (px <= 0 || x1 <= x0 || y1 <= y0) return;
  const sw = x1 - x0;
  const sh = y1 - y0;
  const buf = new Uint8Array(sw * sh);
  for (let y = 0; y < sh; y++) {
    buf.set(mask.subarray((y0 + y) * w + x0, (y0 + y) * w + x1), y * sw);
  }
  const line = new Uint8Array(Math.max(sw, sh));
  // horizontal pass
  for (let y = 0; y < sh; y++) {
    const off = y * sw;
    let run = 0;
    for (let i = 0; i < sw + px; i++) {
      if (i < sw && buf[off + i]) run++;
      const drop = i - 2 * px - 1;
      if (drop >= 0 && buf[off + drop]) run--;
      const o = i - px;
      if (o >= 0) line[o] = run > 0 ? 1 : 0;
    }
    buf.set(line.subarray(0, sw), off);
  }
  // vertical pass
  for (let x = 0; x < sw; x++) {
    let run = 0;
    for (let i = 0; i < sh + px; i++) {
      if (i < sh && buf[i * sw + x]) run++;
      const drop = i - 2 * px - 1;
      if (drop >= 0 && buf[drop * sw + x]) run--;
      const o = i - px;
      if (o >= 0) line[o] = run > 0 ? 1 : 0;
    }
    for (let y = 0; y < sh; y++) buf[y * sw + x] = line[y];
  }
  for (let y = 0; y < sh; y++) {
    mask.set(buf.subarray(y * sw, y * sw + sw), (y0 + y) * w + x0);
  }
}

const clip1e9 = (v: number) => Math.min(Math.max(v, -1e9), 1e9);

/** Half-open pixel rectangle, always clamped to the frame. */
export interface Rect { x0: number; y0: number; x1: number; y1: number }

export const EMPTY_RECT: Rect = { x0: 0, y0: 0, x1: 0, y1: 0 };

function unionRect(a: Rect, b: Rect): Rect {
  if (a.x1 <= a.x0 || a.y1 <= a.y0) return b;
  if (b.x1 <= b.x0 || b.y1 <= b.y0) return a;
  return {
    x0: Math.min(a.x0, b.x0), y0: Math.min(a.y0, b.y0),
    x1: Math.max(a.x1, b.x1), y1: Math.max(a.y1, b.y1),
  };
}

/**
 * Rasterize one region for one pose into `mask` (which must be zeroed).
 * → ok=false means a projection was non-finite (fail closed). `rect` bounds every
 * pixel this call could have touched, so callers can count/clear/read back a
 * window instead of the whole 1280² frame.
 */
export function regionMask(
  region: PreparedRegion, pose: Float64Array, poseOff: number,
  cam: Cam, w: number, h: number, dilatePx: number, mask: Uint8Array,
): { ok: boolean; rect: Rect } {
  let bx0 = Infinity, by0 = Infinity, bx1 = -Infinity, by1 = -Infinity;
  for (const tri of region.tris) {
    const pc = clipNear(worldToCam(tri, pose, poseOff));
    if (pc.length < 9) continue; // entirely behind the camera
    const img = project(pc, cam);
    for (let i = 0; i < img.length; i++) if (!Number.isFinite(img[i])) return { ok: false, rect: EMPTY_RECT };
    fillPolygon(img, w, h, mask);
    // clip in float first: a grazing near-plane vertex can project to ~1e300,
    // which overflows an integer cast and would silently skip the dilation
    let mnx = Infinity, mny = Infinity, mxx = -Infinity, mxy = -Infinity;
    for (let i = 0; i < img.length; i += 2) {
      if (img[i] < mnx) mnx = img[i];
      if (img[i] > mxx) mxx = img[i];
      if (img[i + 1] < mny) mny = img[i + 1];
      if (img[i + 1] > mxy) mxy = img[i + 1];
    }
    bx0 = Math.min(bx0, clip1e9(Math.floor(mnx)));
    by0 = Math.min(by0, clip1e9(Math.floor(mny)));
    bx1 = Math.max(bx1, clip1e9(Math.ceil(mxx)));
    by1 = Math.max(by1, clip1e9(Math.ceil(mxy)));
  }
  if (!Number.isFinite(bx0)) return { ok: true, rect: EMPTY_RECT };
  // dilate only inside the region's bbox grown by the margin — identical result,
  // far cheaper than a full-frame pass
  const x0 = Math.max(0, Math.min(w, bx0 - dilatePx - 1));
  const y0 = Math.max(0, Math.min(h, by0 - dilatePx - 1));
  const x1 = Math.max(0, Math.min(w, bx1 + dilatePx + 2));
  const y1 = Math.max(0, Math.min(h, by1 + dilatePx + 2));
  if (dilatePx > 0) dilateRect(mask, w, h, x0, y0, x1, y1, dilatePx);
  return { ok: true, rect: { x0, y0, x1, y1 } };
}

export interface FrameMask {
  /** w*h, 1 = black this pixel. */
  mask: Uint8Array;
  /** Masked pixel count per region (w*h each when failed closed). */
  counts: number[];
  /** True when the whole frame was blacked because data was missing/bad. */
  failedClosed: boolean;
  /** Total masked pixels in the union. */
  total: number;
  /** Bounds every set pixel — the only window a consumer needs to touch. */
  rect: Rect;
}

/**
 * The per-frame masker. Owns the scratch buffers so a long encode does not
 * churn 1.6 MB allocations per frame.
 */
export class FrameMasker {
  readonly w: number;
  readonly h: number;
  readonly regions: PreparedRegion[];
  readonly cam: Cam;
  readonly dilatePx: number;
  private union: Uint8Array;
  private scratch: Uint8Array;
  /** Windows written by the PREVIOUS call — all that needs zeroing next time. */
  private dirty: Rect = EMPTY_RECT;
  private scratchDirty: Rect = EMPTY_RECT;

  constructor(
    regions: PreparedRegion[],
    cam: Cam,
    w: number,
    h: number,
    dilatePx: number = DEFAULT_DILATE_PX,
  ) {
    this.regions = regions;
    this.cam = cam;
    this.dilatePx = dilatePx;
    this.w = w;
    this.h = h;
    this.union = new Uint8Array(w * h);
    this.scratch = new Uint8Array(w * h);
  }

  /**
   * → the union mask for one frame. `pose`/`poseOff` index into a CamTrack's
   * pose array; `valid` is (pose valid AND pairing within tolerance).
   *
   * The returned mask is REUSED between calls — copy it if you need to keep it.
   */
  frame(pose: Float64Array, poseOff: number, valid: boolean): FrameMask {
    const { w, h, union, scratch, regions } = this;
    const npx = w * h;
    const full: Rect = { x0: 0, y0: 0, x1: w, y1: h };
    // Only the previous frame's dirty window can be non-zero — clearing that
    // instead of 1.6 MB per region per frame is most of this loop's budget.
    clearRect(union, w, this.dirty);
    this.dirty = EMPTY_RECT;
    if (!valid) {
      // FAIL CLOSED: no pose for this frame → black the whole frame. Never leave
      // a frame unmasked because tracking data was missing.
      union.fill(1);
      this.dirty = full;
      return { mask: union, counts: regions.map(() => npx), failedClosed: true, total: npx, rect: full };
    }
    const counts: number[] = [];
    let dirty = EMPTY_RECT;
    for (const region of regions) {
      clearRect(scratch, w, this.scratchDirty);
      const { ok, rect } = regionMask(region, pose, poseOff, this.cam, w, h, this.dilatePx, scratch);
      this.scratchDirty = rect;
      if (!ok) {
        union.fill(1);
        this.dirty = full;
        return { mask: union, counts: regions.map(() => npx), failedClosed: true, total: npx, rect: full };
      }
      let c = 0;
      for (let y = rect.y0; y < rect.y1; y++) {
        const row = y * w;
        for (let x = rect.x0; x < rect.x1; x++) {
          if (scratch[row + x]) { c++; union[row + x] = 1; }
        }
      }
      counts.push(c);
      dirty = unionRect(dirty, rect);
    }
    let total = 0;
    for (let y = dirty.y0; y < dirty.y1; y++) {
      const row = y * w;
      for (let x = dirty.x0; x < dirty.x1; x++) if (union[row + x]) total++;
    }
    this.dirty = dirty;
    return { mask: union, counts, failedClosed: false, total, rect: dirty };
  }
}

function clearRect(buf: Uint8Array, w: number, r: Rect): void {
  for (let y = r.y0; y < r.y1; y++) buf.fill(0, y * w + r.x0, y * w + r.x1);
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-eye plan (everything the video pass needs, resolved from the session)
// ─────────────────────────────────────────────────────────────────────────────

export interface EyePlan {
  eye: Eye;
  cam: Cam;
  masker: FrameMasker;
  track: CamTrack;
  idx: Int32Array;
  /** pairing-in-tolerance AND pose-valid, per video frame. */
  ok: Uint8Array;
  /** Frame timestamps (ns) — one per emitted video frame. */
  vidTs: number[];
  frames: number;
}

export interface PlanOpts {
  dilatePx?: number;
  maxDtMs?: number;
  timeOffsetMs?: number;
  /** Override the raster size when the video disagrees with meta.json. */
  width?: number;
  height?: number;
}

/**
 * Resolve everything needed to mask one eye: intrinsics, the region fans, the
 * timestamp pairing, and the fail-closed flags. Pure data — no video touched.
 */
export async function planEye(
  src: SessionSource, eye: Eye, meta: any, masks: MaskDoc, opts: PlanOpts = {},
): Promise<EyePlan> {
  let cam = buildCam(meta, eye);
  const w = opts.width ?? cam.w;
  const h = opts.height ?? cam.h;
  if (w !== cam.w || h !== cam.h) cam = { ...cam, w, h };
  const regions = prepareRegions(masks.regions);
  const vidTs = await loadTimestamps(src, eye);
  const track = await loadCamTrack(src, eye);
  const offMs = opts.timeOffsetMs ?? Number(meta?.pose_time_offset_ms ?? 0) ?? 0;
  const { idx, inTol } = pairFrames(
    vidTs, track.t, Math.round(offMs * 1e6),
    Math.round((opts.maxDtMs ?? DEFAULT_MAX_DT_MS) * 1e6),
  );
  const ok = new Uint8Array(vidTs.length);
  for (let i = 0; i < vidTs.length; i++) ok[i] = inTol[i] && track.valid[idx[i]] ? 1 : 0;
  return {
    eye, cam, track, idx, ok, vidTs, frames: vidTs.length,
    masker: new FrameMasker(regions, cam, w, h, opts.dilatePx ?? DEFAULT_DILATE_PX),
  };
}

/** The mask for video frame `f` of a planned eye. */
export function maskForFrame(plan: EyePlan, f: number): FrameMask {
  return plan.masker.frame(plan.track.pose, plan.idx[f] * 7, plan.ok[f] === 1);
}
